import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

const PullToRefresh = ({ onRefresh, children, threshold = 80 }) => {
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [startY, setStartY] = useState(0);
  const containerRef = useRef(null);

  const handleTouchStart = (e) => {
    if (containerRef.current?.scrollTop === 0) {
      setStartY(e.touches[0].clientY);
    }
  };

  const handleTouchMove = useCallback((e) => {
    if (isRefreshing || containerRef.current?.scrollTop > 0) return;

    const currentY = e.touches[0].clientY;
    const distance = currentY - startY;

    if (distance > 0) {
      e.preventDefault();
      setPullDistance(Math.min(distance, threshold * 1.5));
      setIsPulling(distance > 10);
    }
  }, [isRefreshing, startY, threshold]);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance > threshold && !isRefreshing) {
      setIsRefreshing(true);
      onRefresh().finally(() => {
        setIsRefreshing(false);
        setIsPulling(false);
        setPullDistance(0);
      });
    } else {
      setIsPulling(false);
      setPullDistance(0);
    }
  }, [pullDistance, threshold, isRefreshing, onRefresh]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchEnd, handleTouchMove]);

  const refreshProgress = Math.min(pullDistance / threshold, 1);
  const shouldTrigger = pullDistance > threshold;

  return (
    <div ref={containerRef} className="min-h-full overflow-auto">
      {/* Pull to refresh indicator */}
      <div 
        className="flex items-center justify-center transition-all duration-200 ease-out"
        style={{
          height: isPulling || isRefreshing ? `${Math.max(pullDistance, isRefreshing ? 60 : 0)}px` : '0px',
          opacity: isPulling || isRefreshing ? 1 : 0,
        }}
      >
        <div className="flex flex-col items-center space-y-2 text-primary-600">
          <ArrowPathIcon 
            className={`w-6 h-6 transition-transform duration-200 ${
              isRefreshing ? 'animate-spin' : shouldTrigger ? 'rotate-180' : ''
            }`}
            style={{
              transform: `rotate(${isRefreshing ? 0 : refreshProgress * 180}deg)`,
            }}
          />
          <span className="text-sm font-medium">
            {isRefreshing ? 'Refreshing...' : shouldTrigger ? 'Release to refresh' : 'Pull to refresh'}
          </span>
        </div>
      </div>
      
      {/* Main content */}
      <div className={`transition-transform duration-200 ${isPulling ? 'transform' : ''}`}>
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;